// ── THEME (fp-nofodesk-9) — extracted from App.js, behavior unchanged ──
import { createContext, useContext } from "react";
import { TOKENS, OP_TOKENS, SKELETON } from "./tokens";

export const FONT_H    = TOKENS.fontHeading;
export const FONT_UI   = TOKENS.fontUI;
export const FONT_MONO = TOKENS.fontMono;

// ── Themes ────────────────────────────────────────────────────
const DARK = {
  mode: "navy",
  bgApp:      TOKENS.bgApp,      bgHeader:   TOKENS.bgHeader,  bgSurface:  TOKENS.bgCard,
  bgElevated: TOKENS.bgElevated, border:     TOKENS.border,    borderDim:  TOKENS.bgCard,
  text:       TOKENS.textPrimary,   textBody:   "#D8D6F0",  textSub:    "#9896C8",
  textMuted:  TOKENS.textMuted,     textFaint:  "#4D4A80",  textGhost:  TOKENS.textDisabled,
  textShadow: "#1A1740",            inputBg:    TOKENS.bgCard,
  gold:       TOKENS.gold,          slateBlue:  TOKENS.slateBlue,
  tableRowA:  "#040910",            tableRowB:  "#070D1A",  tableLabelBg: "#040910",
  sectionHdr: TOKENS.bgElevated,
  clPanelBg:  "#080F1E",  clPanelBorder: "#2D28A0",
  clCheckedBg:"#052e16",  clCheckedBorder:"#166534", clCheckedText:"#4ade80",
  clUnchBg:   TOKENS.bgCard,  clUnchBorder:  TOKENS.border,
  elPanelBg:  "#080F1E",  elPanelBorder: "#0f2e0f",
  elItemBg:   TOKENS.bgCard,
  elGapsBg:   "#1a0808",  elGapsBorder:  "#7f1d1d",
  elRecsBg:   "#052e16",  elRecsBorder:  "#166534",
  conflictBg: "#1a0808",  parseBg:       "#0D0E20",
  sbBg: TOKENS.bgApp,  sbBorder: TOKENS.border,  sbText: TOKENS.textPrimary,
  sbMuted: TOKENS.textMuted,  sbHover: "rgba(26,35,64,0.5)",  sbActive: TOKENS.bgElevated,
};

const BLACK = {
  mode: "black",
  bgApp:      "#000000",  bgHeader:   "#000000",  bgSurface:  "#0C0C0C",
  bgElevated: "#141414",  border:     "#1C1C1C",  borderDim:  "#0C0C0C",
  text:       TOKENS.textPrimary,   textBody:   "#D8D6F0",  textSub:    "#9896C8",
  textMuted:  TOKENS.textMuted,     textFaint:  "#4D4A80",  textGhost:  TOKENS.textDisabled,
  textShadow: "#111111",            inputBg:    "#0C0C0C",
  gold:       TOKENS.gold,          slateBlue:  TOKENS.slateBlue,
  tableRowA:  "#000000",            tableRowB:  "#080808",  tableLabelBg: "#000000",
  sectionHdr: "#141414",
  clPanelBg:  "#0C0C0C",  clPanelBorder: "#2D28A0",
  clCheckedBg:"#052e16",  clCheckedBorder:"#166534", clCheckedText:"#4ade80",
  clUnchBg:   "#0C0C0C",  clUnchBorder:  "#1C1C1C",
  elPanelBg:  "#0C0C0C",  elPanelBorder: "#0f2e0f",
  elItemBg:   "#0C0C0C",
  elGapsBg:   "#1a0808",  elGapsBorder:  "#7f1d1d",
  elRecsBg:   "#052e16",  elRecsBorder:  "#166534",
  conflictBg: "#1a0808",  parseBg:       "#0C0C0C",
  sbBg: "#000000",  sbBorder: "#1C1C1C",  sbText: TOKENS.textPrimary,
  sbMuted: TOKENS.textMuted,  sbHover: "rgba(28,28,28,0.8)",  sbActive: "#141414",
};

// Light mode. Neutrals carry a slight indigo bias rather than being pure grey,
// so they read as chosen and stay in the same family as the dark themes; the
// gold accent and the status colours are held constant across all three so a
// component never has to know which theme it is in.
const LIGHT = {
  mode: "light",
  bgApp:      "#F6F6FB",  bgHeader:   "#FFFFFF",  bgSurface:  "#FFFFFF",
  bgElevated: "#FFFFFF",  border:     "#E4E2EC",  borderDim:  "#EEEDF5",
  text:       "#141221",            textBody:   "#2E2B3F",  textSub:    "#565270",
  textMuted:  "#75718A",            textFaint:  "#A6A2B8",  textGhost:  "#C6C3D4",
  textShadow: "#D9D7E4",            inputBg:    "#FFFFFF",
  gold:       TOKENS.gold,          slateBlue:  TOKENS.slateBlue,
  tableRowA:  "#FFFFFF",            tableRowB:  "#F6F6FB",  tableLabelBg: "#F0EFF7",
  sectionHdr: "#F0EFF7",
  clPanelBg:  "#FFFFFF",  clPanelBorder: "#C5BFF2",
  clCheckedBg:"#E9F7EF",  clCheckedBorder:"#8ACFA9", clCheckedText:"#136A3C",
  clUnchBg:   "#FFFFFF",  clUnchBorder:  "#E4E2EC",
  elPanelBg:  "#FFFFFF",  elPanelBorder: "#BFE0C7",
  elItemBg:   "#F6F6FB",
  elGapsBg:   "#FDECEC",  elGapsBorder:  "#F0B5B5",
  elRecsBg:   "#E9F7EF",  elRecsBorder:  "#8ACFA9",
  conflictBg: "#FDECEC",  parseBg:       "#F2F1F9",
  sbBg: "#FFFFFF",  sbBorder: "#E4E2EC",  sbText: "#141221",
  sbMuted: "#75718A",  sbHover: "rgba(91,79,233,0.07)",  sbActive: "#F0EFF7",
};

const THEME_MAP = { navy: DARK, black: BLACK, light: LIGHT };
export const ThemeCtx = createContext(DARK);
export const useT = () => useContext(ThemeCtx);

// For components written against TOKENS, the fixed dark palette. Those kept
// their dark cards and inputs in light mode, and their light headings went
// near-invisible on a white page. This returns TOKENS with every colour taken
// from the active theme instead; radii, sizes and fonts pass through as they
// are, because they do not change with the mode.
export function useThemedTokens() {
  const t = useT();
  return {
    ...TOKENS,
    bgApp: t.bgApp, bgCard: t.bgSurface, bgElevated: t.bgElevated, bgHeader: t.bgHeader, inputBg: t.inputBg,
    textPrimary: t.text, textSecondary: t.textBody, textMuted: t.textMuted, textDisabled: t.textGhost,
    border: t.border, borderLight: t.border, gold: t.gold,
    // Loading placeholders follow the theme too, or they show as dark blocks.
    shimmerBase: t.mode === "light" ? t.borderDim : SKELETON.base,
    shimmerHighlight: t.mode === "light" ? t.bgApp : SKELETON.highlight,
  };
}
export const OP = OP_TOKENS;
export { DARK, BLACK, LIGHT, THEME_MAP };
